import type { ToolbarProps } from 'react-big-calendar';
import { Navigate } from 'react-big-calendar';
import { useMedia } from 'react-use';
import { useContext } from 'react';
import {
  Button,
  Flex,
  Heading,
  IconArrowLeftOutline,
  IconButton,
  IconChevronLeft,
  IconChevronRight,
  IconCog,
  IconCogOutline,
  IconPlusOutline,
  Paragraph,
  Spacer,
  TextButton,
  ThemeConstellation,
} from '@zillow/constellation';
import { Box, Paragraph as ParagraphC10 } from '@zillow/constellation-10';
import { Link } from 'react-router-dom';
import { differenceInCalendarWeeks, isSameDay, isSameWeek } from 'date-fns';

import { StyledToolbarHeader } from './StyledComponents';
import {
  getTourCalendarDescription,
  TOUR_CALENDAR_HEADER,
  TOUR_CALENDAR_MODES,
  type ZCalendarEvent,
} from '../../../../constants/TourCalendar';
import usePathsManifest from '../../../../hooks/usePathsManifest';

import TourCalendarContext from '../../../TourCalendarWithSettings/context/TourCalendarContext';

type TourCalendarToolbarProps = {
  mode: TOUR_CALENDAR_MODES;
  onClickSettings?: (shouldDisplaySettings: boolean) => void;
  onAddToYourAvailabilityClick: () => void;
  listingAlias: string;
  isInMobileWebView?: boolean;
  now: Date;
  lastActivatedDate: Date;
} & ToolbarProps<ZCalendarEvent>;
const TourCalendarToolbar = (props: TourCalendarToolbarProps) => {
  const { listingAddress } = useContext(TourCalendarContext);
  const { paths } = usePathsManifest('/rental-manager');
  const {
    mode,
    onClickSettings,
    onAddToYourAvailabilityClick,
    listingAlias,
    isInMobileWebView,
    now,
    lastActivatedDate,
    date: calendarDate,
  } = props;
  const { breakpoints } = ThemeConstellation.ns();
  const isScreenLarge = useMedia(`(min-width: ${breakpoints.lg}px)`);
  const isFullCalendar = mode === TOUR_CALENDAR_MODES.FULL_CALENDAR;
  const showBackNavigation = listingAddress && !isInMobileWebView && mode !== TOUR_CALENDAR_MODES.TOUR_AVAILABILITY;

  const onTodayClick = () => {
    props.onNavigate(Navigate.TODAY);
  };

  const onClickBack = () => {
    props.onNavigate(Navigate.PREVIOUS);
  };

  const onClickNext = () => {
    props.onNavigate(Navigate.NEXT);
  };

  if (isScreenLarge) {
    return (
      <StyledToolbarHeader
        display="flex"
        padding="md"
        alignItems="center"
        justifyContent="space-between"
        marginBottom="xs"
        isScreenLarge={isScreenLarge}
      >
        <Flex display="flex" alignItems="center" gap="xs">
          {showBackNavigation && (
            <IconButton
              as={Link}
              to={`${paths.propertiesPage}/${listingAlias}/dashboard/overview`}
              buttonType="tertiary"
              bare
              icon={<IconArrowLeftOutline />}
              data-testid="TourCalendarToolbar-HistoryBackBtn"
              title={`Back to ${listingAddress}`}
            />
          )}
          <Button
            buttonType="tertiary"
            onClick={onTodayClick}
            data-testid="TourCalendarToolbar-TodayBtn"
            marginLeft="sm"
          >
            Today
          </Button>
          <Spacer marginLeft="sm">
            <IconButton
              buttonType="tertiary"
              bare
              icon={<IconChevronLeft />}
              title="Previous week"
              onClick={onClickBack}
              disabled={isSameWeek(calendarDate, lastActivatedDate)}
              data-testid="TourCalendarToolbar-BackBtn"
            />
          </Spacer>
          <IconButton
            buttonType="tertiary"
            bare
            icon={<IconChevronRight />}
            title="Next week"
            onClick={onClickNext}
            disabled={differenceInCalendarWeeks(calendarDate, now) >= 3}
            data-testid="TourCalendarToolbar-NextBtn"
          />
          <Box css={{ display: 'flex', flexDirection: 'column', marginLeft: 'default' }}>
            <Heading level={5}>{props.label}</Heading>
            {listingAddress && (
              <ParagraphC10
                textStyle="body-xs"
                css={{ color: 'text.subtle' }}
                data-testid="TourCalendarToolbar-ListingAddress"
              >
                {listingAddress}
              </ParagraphC10>
            )}
          </Box>
        </Flex>
        <Flex display="flex" alignItems="center" gap="md" marginLeft="sm">
          {isFullCalendar && (
            <TextButton
              buttonType="secondary"
              icon={<IconCog />}
              onClick={() => onClickSettings?.(true)}
              data-testid="TourCalendarToolbar-SettingsBtn"
            >
              Settings
            </TextButton>
          )}
          <Button
            buttonType="primary"
            icon={<IconPlusOutline />}
            onClick={onAddToYourAvailabilityClick}
            data-testid="TourCalendarToolbar-AddAvailabilityBtn"
          >
            Add your availability
          </Button>
        </Flex>
      </StyledToolbarHeader>
    );
  }

  return (
    <StyledToolbarHeader
      display="flex"
      flexDirection="column"
      paddingX="sm"
      paddingBottom="sm"
      isScreenLarge={isScreenLarge}
    >
      {mode === TOUR_CALENDAR_MODES.TOUR_AVAILABILITY && (
        <>
          <Heading level="4" fontFamily="serif" marginBottom="xs" data-testid="TourCalendarToolbar-Header">
            {TOUR_CALENDAR_HEADER}
          </Heading>
          <Paragraph fontColor="textMedium" marginBottom="sm" data-testid="TourCalendarToolbar-Description">
            {getTourCalendarDescription(true)}
          </Paragraph>
        </>
      )}
      <>
        {showBackNavigation && (
          <Spacer marginTop="sm">
            <TextButton
              as={Link}
              to={`${paths.propertiesPage}/${listingAlias}/dashboard/overview`}
              icon={<IconChevronLeft />}
              data-testid="TourCalendarToolbar-HistoryBackBtn-Mobile"
            >
              Back to {listingAddress}
            </TextButton>
          </Spacer>
        )}
        <Flex display="flex" alignItems="center" justifyContent="space-between" marginTop="sm">
          <Paragraph fontType="bodyHeading" data-testid="TourCalendarToolbar-DayLabel-Mobile">
            {isSameDay(calendarDate, now) ? 'Today' : props.label}
          </Paragraph>
          <Flex display="flex" gap="xs">
            <IconButton
              buttonType="tertiary"
              bare
              size="xs"
              icon={<IconChevronLeft />}
              title="Previous week"
              onClick={onClickBack}
              disabled={isSameDay(calendarDate, lastActivatedDate)}
              data-testid="TourCalendarToolbar-BackBtn-Mobile"
            />
            <IconButton
              buttonType="tertiary"
              bare
              size="xs"
              icon={<IconChevronRight />}
              title="Next week"
              onClick={onClickNext}
              disabled={differenceInCalendarWeeks(calendarDate, now) >= 3}
              data-testid="TourCalendarToolbar-NextBtn-Mobile"
            />
            {isFullCalendar && (
              <TextButton
                buttonType="secondary"
                icon={<IconCogOutline size="xs" />}
                onClick={() => onClickSettings?.(true)}
                data-testid="TourCalendarToolbar-SettingsBtn-Mobile"
              >
                Settings
              </TextButton>
            )}
          </Flex>
        </Flex>
      </>
    </StyledToolbarHeader>
  );
};

export default TourCalendarToolbar;
